import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

const MACHINE_LABELS = {
  hook: 'Hook', three_claw: '3-Claw', two_claw: '2-Claw',
  jewel_catcher: 'Jewel', push: 'Pusher', unknown: 'Unknown',
};

function PartnerScans() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scans, setScans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cursors, setCursors] = useState([null]);
  const [page, setPage] = useState(0);

  const limit = 20;

  const fetchScans = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('limit', limit);
    const cursor = cursors[page];
    if (cursor) params.set('cursor', cursor);
    const q = searchParams.get('q');
    if (q) params.set('q', q);

    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/partner/scans?${params}`);
      const json = await res.json();
      const d = json.data || json;
      setScans(d.items || []);
      setTotal(d.total || 0);
      if (d.nextCursor && cursors.length <= page + 1) {
        setCursors((prev) => [...prev, d.nextCursor]);
      }
    } catch {
      // handled by apiFetch
    } finally {
      setLoading(false);
    }
  }, [searchParams, page, cursors]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  const handleSearch = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setSearchParams({ q: fd.get('q') || '' });
    setPage(0);
    setCursors([null]);
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0">My Scans</h4>
        <span className="text-muted" style={{ fontSize: '0.8125rem' }}>{total} records</span>
      </div>

      <form onSubmit={handleSearch} className="mb-3">
        <div className="input-group" style={{ maxWidth: 360 }}>
          <input name="q" className="form-control form-control-sm" placeholder="Search..." defaultValue={searchParams.get('q') || ''} />
          <button className="btn btn-outline-secondary btn-sm" type="submit">Search</button>
        </div>
      </form>

      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.8125rem' }}>
              <thead className="table-light">
                <tr>
                  <th>Image</th>
                  <th>Prize</th>
                  <th>Machine</th>
                  <th>Status</th>
                  <th>Scanned</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.imageUrl ? (
                        <img src={s.imageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        <div style={{ width: 48, height: 48, background: '#f0f0f0', borderRadius: 4 }} />
                      )}
                    </td>
                    <td>{s.prizeName || '-'}</td>
                    <td>{MACHINE_LABELS[s.machineType] || s.machineType}</td>
                    <td>
                      <span className={`badge bg-${s.status === 'completed' ? 'success' : s.status === 'failed' ? 'danger' : 'warning'}`}>
                        {s.status}
                      </span>
                    </td>
                    <td>{new Date(s.scannedAt).toLocaleDateString('ja-JP')}</td>
                  </tr>
                ))}
                {scans.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No scans found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {total > limit && (
        <div className="mt-3">
          <Pagination
            page={page}
            hasMore={cursors.length > page + 1}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </div>
      )}
    </Layout>
  );
}

export default PartnerScans;
